/* eslint-disable no-console */
import React, { ComponentRef, useEffect, useRef, useState } from 'react';

import { Box, CircularProgress } from '@mui/material';
import GUI from 'lil-gui';
import { setPreviewZoom, stateOfGeneralSlice } from 'state/general/general.slice';
import { type CoatType } from 'state/general/general.types';
import Stats from 'stats-gl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RectAreaLightHelper } from 'three/examples/jsm/helpers/RectAreaLightHelper.js';
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { UltraHDRLoader } from 'three/examples/jsm/loaders/UltraHDRLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { TAARenderPass } from 'three/examples/jsm/postprocessing/TAARenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';

import { type ThreeJSImageProps } from './types';

const PRIMARY_COLOR = '#c3a05f';

export const ThreeJSImage = ({ printColor, printBW, matCap, envTex, noiseTex }: ThreeJSImageProps) => {
   const threeContainer = useRef<ComponentRef<'div'>>(null);
   const [isImageReady, setIsImageReady] = useState(false);
   const physicalMaterialRef = useRef<THREE.MeshPhysicalMaterial | null>(null);
   const basePaperMaterialRef = useRef<THREE.MeshPhysicalMaterial | null>(null);
   const controlsRef = useRef<OrbitControls | null>(null);
   const composerRef = useRef<EffectComposer | null>(null);
   const originalTextureBWRef = useRef<HTMLImageElement | null>(null);
   const blurredTextureRef = useRef<THREE.CanvasTexture | null>(null);

   let scene: THREE.Scene,
      camera: THREE.PerspectiveCamera,
      renderer: THREE.WebGLRenderer,
      composer: EffectComposer,
      controls: OrbitControls,
      basePaperObj: THREE.Mesh,
      coatObj: THREE.Mesh,
      material: THREE.MeshPhysicalMaterial,
      physicalMaterial: THREE.MeshPhysicalMaterial,
      blurredTexture: THREE.CanvasTexture,
      invertedTexture: THREE.CanvasTexture,
      texEnv: THREE.Texture,
      stats: Stats; // performance stats

   let needsRender = false;

   let imgSizes = {
      width: 0,
      height: 0,
   };

   const dispatch = useAppDispatch();
   const { selectedImageView, coatType, coatThickness, previewZoom } = useAppSelector(stateOfGeneralSlice);

   const MIN_DIST = 1;
   const MAX_DIST = 20;
   const DEFAULT_DIST = 7.2;
   const ZOOM_MIN = 25;
   const ZOOM_MAX = 400;
   const distanceToZoom = (d: number) => {
      if (d <= DEFAULT_DIST) return Math.round(100 + (DEFAULT_DIST - d) * (300 / (DEFAULT_DIST - MIN_DIST)));
      return Math.round(100 - (d - DEFAULT_DIST) * (75 / (MAX_DIST - DEFAULT_DIST)));
   };
   const zoomToDistance = (z: number) => {
      if (z >= 100) return Math.max(MIN_DIST, DEFAULT_DIST - (z - 100) * ((DEFAULT_DIST - MIN_DIST) / 300));
      return Math.min(MAX_DIST, DEFAULT_DIST + (100 - z) * ((MAX_DIST - DEFAULT_DIST) / 75));
   };

   const getActiveCoatType = (): CoatType => {
      if (coatType) return coatType;
      if (selectedImageView === 'sense') return 'varnish';
      if (selectedImageView === 'silver foil') return 'silver';
      if (selectedImageView === 'Preview') return 'gold';
      return 'gold';
   };

   const prodConfig = {
      coatThickness: 'mid', // min, mid, max
      coatType: getActiveCoatType(), // clear, silver, gold
      printRatio: 1,
   };

   const PAPER_MAT = {
      bumpScale: 0.2,
      roughness: 0.28,
      displacementScale: 0.0,
      envMapIntensity: 0.8,
      flatShading: false,
      clearcoat: 0.0,
      clearcoatRoughness: 0.0,
      specularIntensity: 0.28,
      specularColor: 0xffffff,
   };

   const COAT_MAT = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.05,
      depthWrite: false,
      depthTest: false,
      color: 0xffffff,
      roughness: 0.0,
      metalness: 0.07,
      // ior: 1.20,
      reflectivity: 0.0,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 1.0,
      clearcoatRoughness: 0.0,
      specularIntensity: 0.0,
      specularColor: 0xffffff,
      flatShading: false,
      transmission: 1,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 2.0,
      displacementScale: 0.0,
      envMapIntensity: 1.8,
      clearcoatNormalScale: new THREE.Vector2(0.005, 0.005),
   };

   const COAT_MAT_gold = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0xa6934e,
      roughness: 0.18,
      metalness: 1.0,
      // ior: 1.20,
      reflectivity: 0.96,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 3.0,
      clearcoatRoughness: 0.0,
      specularIntensity: 0.0,
      specularColor: 0xffffff,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.5,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_silverBlue = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0x6b8fc8,
      roughness: 0.22,
      metalness: 0.9,
      reflectivity: 0.94,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.4,
      clearcoatRoughness: 0.08,
      specularIntensity: 0.2,
      specularColor: 0x9cc4e8,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 5,
      displacementScale: 0.0,
      envMapIntensity: 2.6,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_silver = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0x555555,
      roughness: 0.25,
      metalness: 0.8,
      reflectivity: 0.96,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.2,
      clearcoatRoughness: 0.1,
      specularIntensity: 0.25,
      specularColor: 0xffffff,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 5,
      displacementScale: 0.0,
      envMapIntensity: 2.8,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_bronze = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0xcd7f32,
      roughness: 0.22,
      metalness: 1.0,
      reflectivity: 0.92,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.8,
      clearcoatRoughness: 0.05,
      specularIntensity: 0.2,
      specularColor: 0xffffff,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.6,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_roseGold = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0xb76e79,
      roughness: 0.16,
      metalness: 1.0,
      reflectivity: 0.95,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.6,
      clearcoatRoughness: 0.06,
      specularIntensity: 0.18,
      specularColor: 0xffe4e1,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.5,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_champagne = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0xf7e7ce,
      roughness: 0.14,
      metalness: 1.0,
      reflectivity: 0.96,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.4,
      clearcoatRoughness: 0.05,
      specularIntensity: 0.16,
      specularColor: 0xfffff0,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.4,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_emeraldGreen = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0x50c878,
      roughness: 0.2,
      metalness: 0.95,
      reflectivity: 0.92,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.6,
      clearcoatRoughness: 0.06,
      specularIntensity: 0.2,
      specularColor: 0x90ee90,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.6,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_burgundy = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0x800020,
      roughness: 0.22,
      metalness: 0.95,
      reflectivity: 0.9,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.5,
      clearcoatRoughness: 0.06,
      specularIntensity: 0.18,
      specularColor: 0xa52a2a,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.5,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_lavender = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0x9b8bc8,
      roughness: 0.18,
      metalness: 0.9,
      reflectivity: 0.93,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.4,
      clearcoatRoughness: 0.06,
      specularIntensity: 0.17,
      specularColor: 0xc8b8e0,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.5,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_royalPurple = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0x7851a9,
      roughness: 0.2,
      metalness: 0.95,
      reflectivity: 0.92,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.6,
      clearcoatRoughness: 0.05,
      specularIntensity: 0.2,
      specularColor: 0x9370db,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 1.6,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const COAT_MAT_black = {
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.0,
      depthWrite: false,
      depthTest: false,
      color: 0x1a1a1a,
      roughness: 0.15,
      metalness: 1.0,
      reflectivity: 0.95,
      iridescence: 0,
      iridescenceIOR: 1.3,
      sheen: 0,
      sheenRoughness: 0.1,
      sheenColor: 0x000000,
      clearcoat: 2.8,
      clearcoatRoughness: 0.04,
      specularIntensity: 0.25,
      specularColor: 0x333333,
      flatShading: false,
      transmission: 0,
      thickness: 0.01,
      attenuationColor: 0xffffff,
      attenuationDistance: 0.00001,
      bumpScale: 1.5,
      displacementScale: 0.0,
      envMapIntensity: 2.0,
      clearcoatNormalScale: new THREE.Vector2(0.03, 0.03),
   };

   const resolveCoatMaterial = (type: CoatType) => {
      if (type === 'varnish') return COAT_MAT;
      if (type === 'silver') return COAT_MAT_silver;
      if (type === 'silverBlue') return COAT_MAT_silverBlue;
      if (type === 'bronze') return COAT_MAT_bronze;
      if (type === 'roseGold') return COAT_MAT_roseGold;
      if (type === 'champagne') return COAT_MAT_champagne;
      if (type === 'emeraldGreen') return COAT_MAT_emeraldGreen;
      if (type === 'burgundy') return COAT_MAT_burgundy;
      if (type === 'lavender') return COAT_MAT_lavender;
      if (type === 'royalPurple') return COAT_MAT_royalPurple;
      if (type === 'black') return COAT_MAT_black;
      return COAT_MAT_gold;
   };

   useEffect(() => {
      // react to toolbar selection and initial coat choice
      try {
         const activeCoatType = getActiveCoatType();
         prodConfig.coatType = activeCoatType;

         if (physicalMaterialRef.current) {
            const baseCoatMat = resolveCoatMaterial(activeCoatType);
            // Update existing material parameters and re-render
            physicalMaterialRef.current.setValues(baseCoatMat);
            physicalMaterialRef.current.needsUpdate = true;
            try {
               controlsRef.current && controlsRef.current.update();
            } catch (e) {
               void e;
            }
            try {
               composerRef.current && composerRef.current.render();
            } catch (e) {
               void e;
            }
         }
      } catch (e) {
         void e;
      }
   }, [selectedImageView, coatType]);

   useEffect(() => {
      // React to coat thickness changes
      try {
         if (!originalTextureBWRef.current || !blurredTextureRef.current) {
            return;
         }

         const img = originalTextureBWRef.current;
         const blurredTex = blurredTextureRef.current;

         // Create a new canvas with the updated blur
         const blurCanvas = document.createElement('canvas');
         const blurCtx = blurCanvas.getContext('2d');
         if (!blurCtx) return;

         blurCanvas.width = img.width;
         blurCanvas.height = img.height;

         // Draw the original image
         blurCtx.drawImage(img, 0, 0);

         // Apply blur based on thickness
         let txBlur = '';
         if (coatThickness === 'min') {
            txBlur = 'blur(2px)';
         } else if (coatThickness === 'mid') {
            txBlur = 'blur(4px)';
         } else if (coatThickness === 'max') {
            txBlur = 'blur(6px)';
         }
         blurCtx.filter = txBlur;
         blurCtx.drawImage(blurCanvas, 0, 0);

         // Update the existing texture's image
         blurredTex.image = blurCanvas;
         blurredTex.needsUpdate = true;

         // Update both materials that use this texture
         if (physicalMaterialRef.current) {
            physicalMaterialRef.current.needsUpdate = true;
         }
         if (basePaperMaterialRef.current) {
            basePaperMaterialRef.current.needsUpdate = true;
         }

         // Trigger re-render
         try {
            controlsRef.current && controlsRef.current.update();
         } catch (e) {
            void e;
         }
         try {
            composerRef.current && composerRef.current.render();
         } catch (e) {
            void e;
         }

         console.log('Coat thickness updated to:', coatThickness);
      } catch (e) {
         console.error('Error updating coat thickness:', e);
      }
   }, [coatThickness]);

   useEffect(() => {
      const ctrl = controlsRef.current;
      if (!ctrl) return;
      const cam = ctrl.object as THREE.PerspectiveCamera;
      const dir = cam.position.clone().sub(ctrl.target).normalize();
      const newDist = zoomToDistance(previewZoom);
      cam.position.copy(ctrl.target).add(dir.multiplyScalar(newDist));
      try {
         ctrl.update();
      } catch (e) {
         void e;
      }
      try {
         composerRef.current && composerRef.current.render();
      } catch (e) {
         void e;
      }
   }, [previewZoom]);

   useEffect(() => {
      setIsImageReady(false);
      let onDebugChangeRef: ((e: StorageEvent) => void) | null = null;
      let resizeHandler: (() => void) | null = null;
      let canvas1: HTMLCanvasElement | null = null;
      const texturesToDispose: THREE.Texture[] = [];
      const geometriesToDispose: THREE.BufferGeometry[] = [];
      const materialsToDispose: THREE.Material[] = [];
      let texturesReady = false;

      const init = () => {
         if (!threeContainer.current) return;

         texturesReady = false;

         scene = new THREE.Scene();

         // Camera
         camera = new THREE.PerspectiveCamera(
            45,
            threeContainer.current.clientWidth / threeContainer.current.clientHeight,
            0.01,
            100,
         );
         camera.position.set(0, 0, 5);
         camera.lookAt(scene.position);
         scene.add(camera);
         console.log(camera);

         //             // Renderer
         //             renderer = new THREE.WebGLRenderer({
         //                 antialias: true,
         //                 alpha: true
         //             });
         //             renderer.setSize(threeContainer.current.clientWidth, threeContainer.current.clientHeight);
         //             //renderer.setClearColor(0x0b0b0b);
         //             renderer.toneMapping = 1;
         //             renderer.toneMappingExposure = 1;
         //             threeContainer.current.appendChild(renderer.domElement);

         // 1. Create Renderer with error handling
         try {
            renderer = new THREE.WebGLRenderer({
               antialias: false, // Disable native AA to use SMAA
               alpha: true,
               powerPreference: 'high-performance',
               preserveDrawingBuffer: false,
               failIfMajorPerformanceCaveat: false,
            });
            renderer.setSize(threeContainer.current.clientWidth, threeContainer.current.clientHeight);
            renderer.toneMapping = 1; //THREE.NoToneMapping; // or choose a mapping like THREE.ACESFilmicToneMapping
            renderer.toneMappingExposure = 1;

            // Handle WebGL context loss to prevent errors
            const canvas = renderer.domElement;
            canvas.addEventListener(
               'webglcontextlost',
               (event: Event) => {
                  event.preventDefault();
                  console.warn('WebGL context lost - preventing default');
               },
               false,
            );

            canvas.addEventListener(
               'webglcontextrestored',
               () => {
                  console.warn('WebGL context restored');
               },
               false,
            );

            threeContainer.current.appendChild(renderer.domElement);
         } catch (error) {
            console.error('Error creating WebGL renderer:', error);
            return;
         }

         // 2. Create Composer
         composer = new EffectComposer(renderer);
         composerRef.current = composer;

         // 3. Add RenderPass
         const renderPass = new RenderPass(scene, camera);
         composer.addPass(renderPass);

         // 4. Add UnrealBloomPass
         const bloomPass = new UnrealBloomPass(
            new THREE.Vector2(threeContainer.current.clientWidth, threeContainer.current.clientHeight),
            0.1, // strength
            0.02, // radius
            0.999, // threshold
         );
         //composer.addPass(bloomPass);

         // TAARenderPass needs scene and camera
         const taaRenderPass = new TAARenderPass(scene, camera);

         // Match your renderer size
         taaRenderPass.setSize(threeContainer.current.clientWidth, threeContainer.current.clientHeight);

         // Sample level: higher = better AA, but more expensive (1–5 is typical)
         taaRenderPass.sampleLevel = 2;

         // Optional: enable unbiased rendering (avoids brightness shifts)
         taaRenderPass.unbiased = true;

         composer.addPass(taaRenderPass);

         // Initialize Stats.js only if debug flag enabled
         const debugEnabled = (() => {
            try {
               return localStorage.getItem('debug_3d') === 'true';
            } catch (e) {
               return false;
            }
         })();
         if (debugEnabled) {
            stats = new Stats({
               horizontal: false,
               trackGPU: true,
            });
            Object.assign((stats as any).dom.style, {
               position: 'fixed',
               left: '8px',
               bottom: '90px',
               top: 'auto',
               right: 'auto',
               pointerEvents: 'none',
               zIndex: 2000,
            });
            threeContainer.current.appendChild((stats as any).dom);
         }

         // Controls
         controls = new OrbitControls(camera, renderer.domElement);
         controlsRef.current = controls;
         let zoomDispatchTimeout: ReturnType<typeof setTimeout> | null = null;
         controls.addEventListener('change', () => {
            requestRender(); // ensures a redraw on interaction
            if (zoomDispatchTimeout) clearTimeout(zoomDispatchTimeout);
            zoomDispatchTimeout = setTimeout(() => {
               zoomDispatchTimeout = null;
               const dist = camera.position.distanceTo(controls.target);
               const zoomVal = distanceToZoom(dist);
               const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoomVal));
               dispatch(setPreviewZoom(clamped));
            }, 50);
         });
         controls.enableZoom = true;
         controls.enablePan = true;
         controls.maxPolarAngle = 2.66; // locks vertical rotation
         controls.minPolarAngle = 0.5;
         controls.maxAzimuthAngle = 1.3;
         controls.minAzimuthAngle = -1.3;
         controls.maxDistance = 20;
         controls.minDistance = 1; // Allow much closer zoom (was 3)
         controls.enableDamping = true;
         controls.dampingFactor = 0.05; // rubbery inertia
         controls.rotateSpeed = 0.75;

         const initDist = zoomToDistance(previewZoom);
         const initDir = camera.position.clone().sub(controls.target).normalize();
         camera.position.copy(controls.target).add(initDir.multiplyScalar(initDist));

         // initialize rect light system
         RectAreaLightUniformsLib.init();

         // loading manager
         const manager = new THREE.LoadingManager(() => {
            // render();
         });

         // load environment map
         const loaderEXR = new EXRLoader(manager);
         const matcap_01 = loaderEXR.load(matCap);
         const rgbeLoader = new RGBELoader(manager);
         rgbeLoader.load(envTex, function (loadedEnv: THREE.Texture) {
            loadedEnv.mapping = THREE.EquirectangularReflectionMapping;
            //scene.environment = loadedEnv;
            texEnv = loadedEnv; // assign to outer-scoped variable
            // Track texture for cleanup (it's loaded asynchronously)
            texturesToDispose.push(texEnv);
            console.log('env texture assigned globally:', texEnv);
         });

         const ultraLoader = new UltraHDRLoader();
         (ultraLoader as any).setDataType(THREE.FloatType);
         //
         // Image - texture loader & texture attributes
         const textureLoader = new THREE.TextureLoader(manager);
         textureLoader.crossOrigin = '';

         const texNoise = textureLoader.load(noiseTex);
         texNoise.anisotropy = 16;
         texNoise.wrapS = THREE.RepeatWrapping;
         texNoise.wrapT = THREE.RepeatWrapping;
         texNoise.repeat.set(2, 2);
         texNoise.minFilter = THREE.LinearMipmapLinearFilter;
         texNoise.magFilter = THREE.LinearFilter;
         console.log(texNoise);

         const texColor = textureLoader.load(printColor);
         texColor.anisotropy = 16;
         texColor.minFilter = THREE.LinearMipmapLinearFilter;
         texColor.magFilter = THREE.LinearFilter;
         console.log(texColor);

         const texBW = textureLoader.load(printBW, (texture) => {
            try {
               imgSizes = {
                  width: texture.image.width,
                  height: texture.image.height,
               };
               console.log('texBW loaded:', texBW);

               // Store original image for dynamic blur updates
               originalTextureBWRef.current = texture.image;

               // Create separate canvases for blurred and inverted textures to avoid conflicts
               const blurCanvas = document.createElement('canvas');
               const blurCtx = blurCanvas.getContext('2d')!;
               blurCanvas.width = imgSizes.width;
               blurCanvas.height = imgSizes.height;

               // Draw the image onto the blur canvas
               blurCtx.drawImage(texture.image, 0, 0);

               let txBlur = '';
               if (prodConfig.coatThickness === 'min') {
                  txBlur = 'blur(2px)';
               } else if (prodConfig.coatThickness === 'mid') {
                  txBlur = 'blur(4px)';
               } else if (prodConfig.coatThickness === 'max') {
                  txBlur = 'blur(6px)';
               }
               blurCtx.filter = txBlur;
               blurCtx.drawImage(blurCanvas, 0, 0);
               console.log('coatThickness:', prodConfig.coatThickness);

               blurredTexture = new THREE.CanvasTexture(blurCanvas);
               blurredTexture.anisotropy = 16;
               blurredTexture.minFilter = THREE.LinearMipmapLinearFilter;
               blurredTexture.magFilter = THREE.LinearFilter;
               blurredTexture.needsUpdate = true;
               // Store reference for dynamic updates
               blurredTextureRef.current = blurredTexture;
               // Track texture for cleanup
               texturesToDispose.push(blurredTexture);
               console.log('blurredTexture created');

               // Create separate canvas for inverted texture
               canvas1 = document.createElement('canvas');
               const ctx = canvas1.getContext('2d')!;
               canvas1.width = imgSizes.width;
               canvas1.height = imgSizes.height;

               // Fill with white first
               ctx.fillStyle = 'white';
               ctx.fillRect(0, 0, canvas1.width, canvas1.height);

               // Use difference mode to invert
               ctx.globalCompositeOperation = 'difference';
               ctx.drawImage(texture.image, 0, 0);
               ctx.globalCompositeOperation = 'source-over';

               invertedTexture = new THREE.CanvasTexture(canvas1);
               invertedTexture.anisotropy = 16;
               invertedTexture.minFilter = THREE.LinearMipmapLinearFilter;
               invertedTexture.magFilter = THREE.LinearFilter;
               invertedTexture.needsUpdate = true;
               // Track texture for cleanup
               texturesToDispose.push(invertedTexture);
               console.log('invertedTexture created');

               // Mark textures as ready
               texturesReady = true;
            } catch (error) {
               console.error('Error creating canvas textures:', error);
               texturesReady = false;
            }
         });

         manager.onLoad = function () {
            // Use requestAnimationFrame to ensure textures are fully processed by GPU
            requestAnimationFrame(() => {
               // Double-check that all textures are ready
               if (!blurredTexture || !invertedTexture || !texturesReady) {
                  console.warn('Canvas textures not ready, waiting one more frame...');
                  requestAnimationFrame(() => {
                     if (blurredTexture && invertedTexture && texturesReady) {
                        try {
                           add3DImage(texColor, texBW, texNoise);
                           add_lights();

                           const params = new URLSearchParams(window.location.search);
                           const levelValue = params.get('level');
                           if (levelValue === 'dev') {
                              settings();
                           }
                           render();
                           setIsImageReady(true);
                        } catch (error) {
                           console.error('Error in manager.onLoad callback:', error);
                        }
                     } else {
                        console.error('Canvas textures failed to create after waiting');
                     }
                  });
               } else {
                  try {
                     add3DImage(texColor, texBW, texNoise);
                     add_lights();

                     const params = new URLSearchParams(window.location.search);
                     const levelValue = params.get('level');

                     if (levelValue === 'dev') {
                        settings();
                     }
                     render();
                     setIsImageReady(true);
                  } catch (error) {
                     console.error('Error in manager.onLoad callback:', error);
                  }
               }
            });
         };

         texColor.anisotropy = texBW.anisotropy = 16;
         texColor.colorSpace = 'srgb';
         texColor.minFilter = texBW.minFilter = 1008;
         texColor.magFilter = texBW.magFilter = 1006;

         // Track textures for cleanup (blurredTexture, invertedTexture, and texEnv are added in their callbacks)
         texturesToDispose.push(texColor, texBW, texNoise);

         resizeHandler = onWindowResize;
         window.addEventListener('resize', resizeHandler);
         render();
         // listen for debug flag changes to show/hide stats
         onDebugChangeRef = (e: StorageEvent) => {
            if (!threeContainer.current) return;
            if (e.key && e.key !== 'debug_3d') return;
            const enabled = (() => {
               try {
                  return localStorage.getItem('debug_3d') === 'true';
               } catch (err) {
                  return false;
               }
            })();
            const dom = (stats as any)?.dom;
            if (enabled) {
               if (!dom) {
                  stats = new Stats({ horizontal: false, trackGPU: true });
                  Object.assign((stats as any).dom.style, {
                     position: 'fixed',
                     left: '8px',
                     bottom: '90px',
                     top: 'auto',
                     right: 'auto',
                     pointerEvents: 'none',
                     zIndex: 2000,
                  });
                  threeContainer.current.appendChild((stats as any).dom);
               }
            } else {
               if (dom && dom.parentNode) {
                  dom.parentNode.removeChild(dom);
               }
            }
         };
         window.addEventListener('storage', onDebugChangeRef);
      };

      function add_lights() {
         const light = new THREE.AmbientLight(0xffffff);
         //scene.add(light)

         const d_light2 = new THREE.DirectionalLight(0xffffff, 1);
         d_light2.position.set(-2, -2, 3);
         //scene.add(d_light2)

         const d_light3 = new THREE.DirectionalLight(0xffffff, 1);
         d_light3.position.set(-3, -3, 4);
         //scene.add(d_light3)

         // Enable RectAreaLight support
         RectAreaLightUniformsLib.init();

         // RectAreaLight 1
         const rectLight1 = new THREE.RectAreaLight(0xffffff, 3, 800, 4);
         rectLight1.position.set(1.5, 10, 4);
         rectLight1.lookAt(0, 0, 0);
         //scene.add(rectLight1);

         const helper1 = new RectAreaLightHelper(rectLight1);
         //rectLight1.add(helper1);

         // RectAreaLight 2
         const rectLight2 = new THREE.RectAreaLight(0xffffff, 3, 800, 2);
         rectLight2.position.set(1.75, 3, 4);
         rectLight2.lookAt(0, 0, 0);
         //scene.add(rectLight2);

         const helper2 = new RectAreaLightHelper(rectLight2);
         //rectLight2.add(helper2);

         // RectAreaLight 3
         const rectLight3 = new THREE.RectAreaLight(0xffffff, 2, 800, 5);
         rectLight3.position.set(1, -6, 4);
         rectLight3.lookAt(0, 0, 0);
         //scene.add(rectLight3);

         const helper3 = new RectAreaLightHelper(rectLight3);
         //rectLight3.add(helper3);
      }

      function add3DImage(texColor: THREE.Texture, texBW: THREE.Texture, texNoise: THREE.Texture) {
         try {
            // Validate textures before using them
            if (!texColor || !texBW || !texNoise) {
               console.error('Required textures are missing');
               return;
            }

            if (!blurredTexture || !invertedTexture) {
               console.error('Canvas textures are missing');
               return;
            }

            if (!texEnv) {
               console.error('Environment texture is missing');
               return;
            }

            // Ensure textures are ready
            if (!texColor.image || !texBW.image || !texNoise.image) {
               console.error('Texture images are not loaded');
               return;
            }

            const ceil = 1;
            let geometry;

            if (imgSizes.width > imgSizes.height) {
               const aspect = imgSizes.height / imgSizes.width;
               geometry = new THREE.PlaneGeometry(6, 6 * aspect, Math.floor(ceil / aspect), ceil);
            } else if (imgSizes.width < imgSizes.height) {
               const aspect = imgSizes.width / imgSizes.height;
               geometry = new THREE.PlaneGeometry(6 * aspect, 6, ceil, Math.floor(ceil / aspect));
            } else {
               geometry = new THREE.PlaneGeometry(6, 6, ceil, ceil);
            }

            // Track geometry for cleanup
            geometriesToDispose.push(geometry);

            // Ensure textures are uploaded to GPU
            texColor.needsUpdate = true;
            texBW.needsUpdate = true;
            texNoise.needsUpdate = true;
            blurredTexture.needsUpdate = true;
            invertedTexture.needsUpdate = true;
            texEnv.needsUpdate = true;

            material = new THREE.MeshPhysicalMaterial({
               map: texColor,
               // roughnessMap: texBW,
               bumpMap: blurredTexture,
               envMap: texEnv,
               //displacementMap: blurredTexture,
               ...PAPER_MAT,
            });

            // Mark material as needing update
            material.needsUpdate = true;

            // Store reference for dynamic updates
            basePaperMaterialRef.current = material;

            // Track material for cleanup
            materialsToDispose.push(material);

            basePaperObj = new THREE.Mesh(geometry, material);

            scene.add(basePaperObj);

            const activeCoatType = getActiveCoatType();
            prodConfig.coatType = activeCoatType;
            const baseCoatMat = resolveCoatMaterial(activeCoatType);

            physicalMaterial = new THREE.MeshPhysicalMaterial({
               ...baseCoatMat,
               // displacementMap: blurredTexture,
               bumpMap: blurredTexture,
               alphaMap: invertedTexture,
               envMap: texEnv,
               clearcoatNormalMap: texNoise,
               // normalMap: texNoise
            });

            physicalMaterial.needsUpdate = true;

            // Store reference for dynamic updates
            physicalMaterialRef.current = physicalMaterial;

            // Track material for cleanup
            materialsToDispose.push(physicalMaterial);

            coatObj = new THREE.Mesh(geometry, physicalMaterial);

            scene.add(coatObj);
            //geometry.computeVertexNormals();
            console.log('env texture texEnv: ', texEnv);
            console.log('glass geo: ', geometry);
            console.log(
               'position basePaperObj: ',
               basePaperObj.position,
               ' position coatObj: ',
               coatObj.position,
            );
         } catch (error) {
            console.error('Error in add3DImage:', error);
         }
      }

      function settings() {
         const gui = new GUI();

         const general1 = gui.addFolder('Placeholder');
         general1.close();
         const general = gui.addFolder('PAPER_MAT');
         general.close();
         const physical = gui.addFolder('COAT_MAT');
         physical.close();

         general
            .add(PAPER_MAT, 'bumpScale', -1, 1)
            .step(0.01)
            .onChange((value: number) => {
               material.bumpScale = value;
               render();
            });

         // general.add(PAPER_MAT, 'displacementScale', -0.2, 0.2).step(0.001).onChange((value: number) => {
         //     material.displacementScale = value
         //     render()
         // })

         general.add(PAPER_MAT, 'flatShading').onChange((value: boolean) => {
            material.flatShading = value;
            material.needsUpdate = true;
            render();
         });

         general
            .add(PAPER_MAT, 'roughness', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               material.roughness = value;
               render();
            });

         general
            .add(PAPER_MAT, 'envMapIntensity', 0, 5)
            .step(0.001)
            .onChange((value: number) => {
               material.envMapIntensity = value;
               render();
            });

         physical
            .add(PAPER_MAT, 'clearcoatRoughness', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               material.clearcoatRoughness = value;
               render();
            });

         physical
            .add(PAPER_MAT, 'specularIntensity', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               material.specularIntensity = value;
               render();
            });

         physical.addColor(PAPER_MAT, 'specularColor').onChange((value: string | number | THREE.Color) => {
            material.specularColor.set(value);
            render();
         });

         // Physical
         physical.add(COAT_MAT, 'transparent').onChange((value: boolean) => {
            physicalMaterial.transparent = value;
            render();
         });

         physical
            .add(COAT_MAT, 'opacity', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.opacity = value;
               render();
            });

         physical
            .add(COAT_MAT, 'alphaTest', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.alphaTest = value;
               render();
            });

         // physical.add(COAT_MAT, 'depthWrite').onChange((value: boolean) => {
         //     physicalMaterial.depthWrite = value
         //     render()
         // })

         physical.addColor(COAT_MAT, 'color').onChange((value: string | number | THREE.Color) => {
            physicalMaterial.color.set(value);
            render();
         });

         // physical.addColor(COAT_MAT, 'emissive').onChange((value: string | number | THREE.Color) => {
         //     physicalMaterial.emissive.set(value)
         //     render()
         // })

         physical
            .add(COAT_MAT, 'roughness', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.roughness = value;
               render();
            });

         physical
            .add(COAT_MAT, 'metalness', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.metalness = value;
               render();
            });

         // physical.add(COAT_MAT, 'ior', 1, 2.33).step(0.01).onChange((value: number) => {
         //    physicalMaterial.ior = value
         // })

         physical
            .add(COAT_MAT, 'reflectivity', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.reflectivity = value;
               render();
            });

         physical
            .add(COAT_MAT, 'iridescence', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.iridescence = value;
               render();
            });

         physical
            .add(COAT_MAT, 'iridescenceIOR', 1, 2.33)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.iridescenceIOR = value;
               render();
            });

         physical
            .add(COAT_MAT, 'sheen', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.sheen = value;
               render();
            });

         physical
            .add(COAT_MAT, 'sheenRoughness', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.sheenRoughness = value;
               render();
            });

         physical.addColor(COAT_MAT, 'sheenColor').onChange((value: string | number | THREE.Color) => {
            physicalMaterial.sheenColor.set(value);
            render();
         });

         physical
            .add(COAT_MAT, 'clearcoat', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.clearcoat = value;
               render();
            });

         physical
            .add(COAT_MAT, 'clearcoatRoughness', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.clearcoatRoughness = value;
               render();
            });

         physical
            .add(COAT_MAT, 'specularIntensity', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.specularIntensity = value;
               render();
            });

         physical.addColor(COAT_MAT, 'specularColor').onChange((value: string | number | THREE.Color) => {
            physicalMaterial.specularColor.set(value);
            render();
         });

         physical.add(COAT_MAT, 'flatShading').onChange((value: boolean) => {
            physicalMaterial.flatShading = value;
            physicalMaterial.needsUpdate = true;
            render();
         });

         physical
            .add(COAT_MAT, 'transmission', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.transmission = value;
               render();
            });

         physical
            .add(COAT_MAT, 'thickness', 0, 1)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.thickness = value;
               render();
            });

         physical.addColor(COAT_MAT, 'attenuationColor').onChange((value: string | number | THREE.Color) => {
            physicalMaterial.attenuationColor.set(value);
            render();
         });

         physical
            .add(COAT_MAT, 'attenuationDistance', 0, 0.001)
            .step(0.00001)
            .onChange((value: number) => {
               physicalMaterial.attenuationDistance = value;
               render();
            });

         physical
            .add(COAT_MAT, 'bumpScale', -1, 3)
            .step(0.01)
            .onChange((value: number) => {
               physicalMaterial.bumpScale = value;
               render();
            });

         // physical.add(COAT_MAT, 'displacementScale', -0.005, 0.005).step(0.001).onChange((value: number) => {
         //     physicalMaterial.displacementScale = value
         //     render()
         // })

         physical
            .add(COAT_MAT, 'envMapIntensity', 0, 5)
            .step(0.001)
            .onChange((value: number) => {
               physicalMaterial.envMapIntensity = value;
               render();
            });
      }

      // rendering on request
      function requestRender() {
         if (!needsRender) {
            needsRender = true;
            requestAnimationFrame(() => {
               if (needsRender) {
                  needsRender = false;
                  controls.update();
                  render();
               }
            });
         }
      }

      // main render method
      function render() {
         try {
            // Check if renderer and context are valid
            if (!renderer || !renderer.domElement) {
               console.warn('Renderer not available');
               return;
            }

            const gl = renderer.getContext();
            if (!gl || gl.isContextLost()) {
               console.warn('WebGL context is lost or invalid');
               return;
            }

            if (!composer) {
               console.warn('Composer not available');
               return;
            }

            composer.render();
            //             renderer.render(scene, camera);
            try {
               stats && stats.update();
            } catch (e) {
               void e;
            }
         } catch (error) {
            console.error('Error in render:', error);
         }
      }

      function onWindowResize() {
         if (threeContainer.current) {
            const width = threeContainer.current.clientWidth;
            const height = threeContainer.current.clientHeight;

            renderer.setSize(width, height);
            composer.setSize(width, height);

            camera.aspect = width / height;
            camera.updateProjectionMatrix();

            requestRender(); // to re-render after resize
         }
      }

      init();

      return () => {
         // Remove resize listener
         try {
            if (resizeHandler) {
               window.removeEventListener('resize', resizeHandler);
            }
         } catch (e) {
            void e;
         }

         // Remove debug change listener
         try {
            if (onDebugChangeRef) {
               window.removeEventListener('storage', onDebugChangeRef);
            }
         } catch (e) {
            void e;
         }

         // Dispose of Three.js resources
         try {
            // Remove meshes from scene first to break references
            if (basePaperObj && scene) {
               try {
                  scene.remove(basePaperObj);
                  if (basePaperObj.geometry) basePaperObj.geometry = null as any;
                  if (basePaperObj.material) {
                     if (Array.isArray(basePaperObj.material)) {
                        basePaperObj.material.forEach((mat) => mat.dispose());
                     } else {
                        basePaperObj.material.dispose();
                     }
                  }
               } catch (e) {
                  void e;
               }
            }
            if (coatObj && scene) {
               try {
                  scene.remove(coatObj);
                  if (coatObj.geometry) coatObj.geometry = null as any;
                  if (coatObj.material) {
                     if (Array.isArray(coatObj.material)) {
                        coatObj.material.forEach((mat) => mat.dispose());
                     } else {
                        coatObj.material.dispose();
                     }
                  }
               } catch (e) {
                  void e;
               }
            }

            // Clear scene
            if (scene) {
               try {
                  while (scene.children.length > 0) {
                     scene.remove(scene.children[0]);
                  }
               } catch (e) {
                  void e;
               }
            }

            // Dispose textures
            texturesToDispose.forEach((texture) => {
               try {
                  if (texture && typeof texture.dispose === 'function') {
                     texture.dispose();
                  }
               } catch (e) {
                  void e;
               }
            });

            // Dispose materials
            materialsToDispose.forEach((material) => {
               try {
                  if (material && typeof material.dispose === 'function') {
                     material.dispose();
                  }
               } catch (e) {
                  void e;
               }
            });

            // Dispose geometries
            geometriesToDispose.forEach((geometry) => {
               try {
                  if (geometry && typeof geometry.dispose === 'function') {
                     geometry.dispose();
                  }
               } catch (e) {
                  void e;
               }
            });

            // Dispose controls
            if (controls && typeof controls.dispose === 'function') {
               try {
                  controls.dispose();
               } catch (e) {
                  void e;
               }
            }

            // Dispose composer
            if (composer && typeof composer.dispose === 'function') {
               try {
                  composer.dispose();
               } catch (e) {
                  void e;
               }
            }

            // Dispose renderer (dispose() will handle the context properly)
            if (renderer) {
               try {
                  // Clear any ongoing animations/renders
                  renderer.setAnimationLoop(null);
                  renderer.dispose();
               } catch (e) {
                  void e;
               }
            }

            // Clear canvas reference
            if (canvas1) {
               try {
                  canvas1.width = 0;
                  canvas1.height = 0;
               } catch (e) {
                  void e;
               }
               canvas1 = null;
            }
         } catch (e) {
            console.error('Error disposing Three.js resources:', e);
         }

         // Remove DOM elements
         if (threeContainer.current) {
            try {
               const dom = (stats as any)?.dom;
               if (dom && dom.parentNode) dom.parentNode.removeChild(dom);
            } catch (e) {
               void e;
            }
            try {
               if (
                  renderer &&
                  renderer.domElement &&
                  renderer.domElement.parentNode === threeContainer.current
               ) {
                  threeContainer.current.removeChild(renderer.domElement);
               }
            } catch (e) {
               void e;
            }
         }
      };
   }, [printColor, printBW, matCap, envTex]);
   return (
      <Box
         sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            boxSizing: 'border-box',
            backgroundColor: 'lightgray',
         }}
      >
         <div
            style={{
               width: '100%',
               height: '100%',
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
            }}
            ref={threeContainer}
         />
         {!isImageReady && (
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#d3d3d3',
                  zIndex: 10,
               }}
            >
               <CircularProgress size={48} sx={{ color: PRIMARY_COLOR }} />
            </Box>
         )}
      </Box>
   );
};
